// Venice.ai supported image models (non-NSFW, family-friendly)
// Size support tested against native API: POST https://api.venice.ai/api/v1/image/generate
export type VeniceImageModel =
  | 'nano-banana-pro'
  | 'flux-2-max'
  | 'gpt-image-2'
  | 'seedream-v5-pro'
  | 'flux-2-pro'
  | 'recraft-v4'
  | 'recraft-v4-pro'
  | 'grok-imagine-image-2-0';

export type ImageSize = 'portrait' | 'square' | 'landscape';

export type VeniceResolution = '1K' | '2K' | '4K';
export type VeniceQuality = 'low' | 'medium' | 'high';

export interface ImageSizeConfig {
  width: number;
  height: number;
  aspectRatio: string;
  wordLimit: number;
}

export const SIZE_MAP: Record<ImageSize, ImageSizeConfig> = {
  portrait:  { width: 1024, height: 1792, aspectRatio: '9:16',  wordLimit: 70 },
  square:    { width: 1024, height: 1024, aspectRatio: '1:1',   wordLimit: 40 },
  landscape: { width: 1792, height: 1024, aspectRatio: '16:9',  wordLimit: 70 },
};

const ALL_SIZES: ImageSize[] = ['portrait', 'square', 'landscape'];

/** Map legacy size names to current ImageSize values */
export function normalizeSize(stored: string | null | undefined): ImageSize {
  if (!stored) return 'square';
  if (stored === 'iphone') return 'portrait';
  if (stored === 'desktop') return 'landscape';
  if (stored === 'portrait' || stored === 'square' || stored === 'landscape') return stored;
  return 'square';
}

export interface VeniceModelInfo {
  id: VeniceImageModel;
  name: string;
  description: string;
  cost: number;  // cost in credits (1 credit = $0.02)
  supportedSizes: ImageSize[];
  /** When set, Venice expects `resolution` + `aspect_ratio` instead of pixels. */
  resolution?: VeniceResolution;
  /** Optional quality tier; only send when the model documents support. */
  quality?: VeniceQuality;
}

// Model costs in credits (1 credit = $0.02)
// Pricing: Venice USD cost / 0.02 * 1.10 (10% markup), rounded up, minimum 1
// These are fallback values; production should fetch from model_pricing table
export const VENICE_IMAGE_MODELS: VeniceModelInfo[] = [
  {
    id: 'flux-2-pro',
    name: 'Flux 2 Pro',
    description: 'Professional-grade Flux model with excellent detail and composition',
    cost: 3,  // $0.04 → 3 credits
    supportedSizes: ALL_SIZES,
  },
  {
    id: 'grok-imagine-image-2-0',
    name: 'Grok Imagine 2.0',
    description: "xAI's current image model — strong prompt following and layout at 1K",
    cost: 4,  // $0.07 (1K medium) → 4 credits
    supportedSizes: ALL_SIZES,
    resolution: '1K',
  },
  {
    id: 'recraft-v4',
    name: 'Recraft V4',
    description: 'Professional design-quality image generation with excellent typography',
    cost: 4,  // $0.06 → 4 credits
    supportedSizes: ALL_SIZES,
  },
  {
    id: 'seedream-v5-pro',
    name: 'Seedream V5 Pro',
    description: 'ByteDance production model for layouts, portraits, and multilingual text',
    cost: 4,  // $0.06 (1K) → 4 credits
    supportedSizes: ALL_SIZES,
    resolution: '1K',
  },
  {
    id: 'gpt-image-2',
    name: 'GPT Image 2',
    description: "OpenAI's flagship image model with strong prompt adherence and in-image text",
    cost: 4,  // $0.07 (1K medium) → 4 credits
    supportedSizes: ALL_SIZES,
    resolution: '1K',
    quality: 'medium',
  },
  {
    id: 'flux-2-max',
    name: 'Flux 2 Max',
    description: 'Latest Flux model with maximum quality output',
    cost: 5,  // $0.09 → 5 credits
    supportedSizes: ALL_SIZES,
  },
  {
    id: 'nano-banana-pro',
    name: 'Nano Banana Pro',
    description: 'High-quality photorealistic images with excellent text rendering',
    cost: 10,  // $0.18 (1K) → 10 credits
    supportedSizes: ALL_SIZES,
    resolution: '1K',
  },
  {
    id: 'recraft-v4-pro',
    name: 'Recraft V4 Pro',
    description: 'Premium high-resolution image generation for print-ready quality',
    cost: 16,  // $0.29 → 16 credits
    supportedSizes: ALL_SIZES,
  },
];

export const DEFAULT_VENICE_MODEL: VeniceImageModel = 'grok-imagine-image-2-0';

/** Same-family successors for ids we no longer offer in the picker. */
export const RETIRED_MODEL_MAP: Record<string, VeniceImageModel> = {
  'gpt-image-1-5': 'gpt-image-2',
  'seedream-v4': 'seedream-v5-pro',
  'venice-sd35': 'grok-imagine-image-2-0',
};

// Map legacy provider names to Venice equivalents
// Used for regenerating images created with old /images endpoint
export const LEGACY_MODEL_MAP: Record<string, VeniceImageModel> = {
  'openai': 'gpt-image-2',                 // dall-e-3 / GPT Image 1.5 → current OpenAI model
  'veniceai': 'grok-imagine-image-2-0',    // hidream / venice-sd35 → current default
  'xai': 'grok-imagine-image-2-0',         // grok-2-image → Grok Imagine 2.0
};

export function isOfferedImageModel(
  model: string | null | undefined
): model is VeniceImageModel {
  return !!model && VENICE_IMAGE_MODELS.some((m) => m.id === model);
}

function stripVenicePrefix(storedModel: string): string {
  return storedModel.startsWith('venice:')
    ? storedModel.slice('venice:'.length)
    : storedModel;
}

/** True if we can map this stored/request id onto a currently offered model. */
export function isKnownImageModelId(model: string): boolean {
  const raw = stripVenicePrefix(model);
  return (
    isOfferedImageModel(raw) ||
    raw in RETIRED_MODEL_MAP ||
    raw in LEGACY_MODEL_MAP
  );
}

export function getModelInfo(model: VeniceImageModel): VeniceModelInfo {
  return VENICE_IMAGE_MODELS.find((m) => m.id === model)!;
}

export interface VeniceGenerateBody {
  model: VeniceImageModel;
  prompt: string;
  hide_watermark: true;
  format: 'png';
  aspect_ratio?: string;
  resolution?: VeniceResolution;
  quality?: VeniceQuality;
  width?: number;
  height?: number;
}

/**
 * Build the native Venice `/image/generate` body for a catalog model.
 * Resolution-tier models get `aspect_ratio` + `resolution`; pixel models get
 * width/height; multi-size aspect-ratio models get `aspect_ratio` only.
 */
export function buildVeniceGenerateBody(
  model: VeniceImageModel,
  prompt: string,
  size: ImageSize,
): VeniceGenerateBody {
  const modelInfo = getModelInfo(model);
  const sizeConfig = SIZE_MAP[size];
  const body: VeniceGenerateBody = {
    model,
    prompt,
    hide_watermark: true,
    format: 'png',
  };

  if (modelInfo.resolution) {
    body.aspect_ratio = sizeConfig.aspectRatio;
    body.resolution = modelInfo.resolution;
  } else if (modelInfo.supportedSizes.length > 1) {
    body.aspect_ratio = sizeConfig.aspectRatio;
  } else {
    body.width = sizeConfig.width;
    body.height = sizeConfig.height;
  }

  if (modelInfo.quality) {
    body.quality = modelInfo.quality;
  }

  return body;
}

/**
 * Resolves a stored model string to a valid Venice model.
 * Handles both new format (venice:model-name) and legacy format (provider-name).
 * Returns the model and whether a fallback was used.
 */
export function resolveModel(storedModel: string | null | undefined): {
  model: VeniceImageModel;
  wasRemapped: boolean;
  originalProvider?: string;
} {
  if (!storedModel) {
    return { model: DEFAULT_VENICE_MODEL, wasRemapped: false };
  }

  const raw = stripVenicePrefix(storedModel);

  if (isOfferedImageModel(raw)) {
    return { model: raw, wasRemapped: false };
  }

  if (raw in RETIRED_MODEL_MAP) {
    return {
      model: RETIRED_MODEL_MAP[raw],
      wasRemapped: true,
      originalProvider: storedModel,
    };
  }

  if (raw in LEGACY_MODEL_MAP) {
    return {
      model: LEGACY_MODEL_MAP[raw],
      wasRemapped: true,
      originalProvider: storedModel,
    };
  }

  return { model: DEFAULT_VENICE_MODEL, wasRemapped: true, originalProvider: storedModel };
}
